import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameMap from './logic/GameMap';
import { mapConfigs } from './MapConfig/mapConfigs';
import SafariZoneModal from '../../components/Modals/SafariZoneModal';
import api from '../../api';

const Entrance = () => {
  const [showSafariModal, setShowSafariModal] = useState(false);
  const [playerPosition, setPlayerPosition] = useState({ x: 0, y: 0 });
  const [safariAccess, setSafariAccess] = useState(null);
  const navigate = useNavigate();

  // Function to check Safari Zone access status
  const checkSafariAccess = useCallback(async () => {
    try {
      const response = await api.get('/v1/safari-zone/status', { withCredentials: true });
      setSafariAccess(response.data);
      
      // If user has active access, teleport them to Safari Zone South
      if (response.data.has_access) {
        navigate('/map/SafariZoneSouth');
      }
    } catch (error) {
      console.error('Error checking Safari Zone access:', error);
    }
  }, [navigate]);

  // Check access when component mounts
  useEffect(() => {
    checkSafariAccess();
  }, [checkSafariAccess]);

  // Handle NPC interaction to show Safari Zone modal
  const handleNpcInteraction = (npcId, playerPos) => {
    if (npcId === 'SafraiZone') {
      setPlayerPosition(playerPos);
      // Only show modal if user doesn't already have access
      if (!safariAccess?.has_access) {
        setShowSafariModal(true);
      } else {
        // If they already have access, just navigate directly
        navigate('/map/SafariZoneSouth');
      }
    }
  };

  // Handle successful Safari Zone access purchase
  const handleAccessGranted = (accessData) => {
    // Update access status
    setSafariAccess({
      has_access: true,
      expiration_time: accessData.expiration_time,
      remaining_seconds: accessData.remaining_seconds
    });
    
    // Navigate to Safari Zone South
    setTimeout(() => {
      navigate('/map/SafariZoneSouth');
    }, 500); // Small delay for better UX
  };

  // Function to spawn floating text (passed to modal)
  const spawnText = (text, x, y, color) => {
    // Create a floating text element
    const textElement = document.createElement('div');
    textElement.innerText = text;
    textElement.style.position = 'absolute';
    textElement.style.left = `${x}px`;
    textElement.style.top = `${y}px`;
    textElement.style.color = color || '#ffffff';
    textElement.style.fontSize = '16px';
    textElement.style.fontWeight = 'bold';
    textElement.style.textShadow = '1px 1px 2px rgba(0, 0, 0, 0.7)';
    textElement.style.zIndex = 1000;
    textElement.style.pointerEvents = 'none';
    textElement.style.animation = 'floatUp 2s forwards';
    
    // Add the element to the DOM
    document.body.appendChild(textElement);
    
    // Remove the element after animation completes
    setTimeout(() => {
      document.body.removeChild(textElement);
    }, 2000);
  };

  // Add CSS for floating text animation if it doesn't exist
  useEffect(() => {
    if (!document.getElementById('float-text-style')) {
      const style = document.createElement('style');
      style.id = 'float-text-style';
      style.innerHTML = `
        @keyframes floatUp {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-30px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const styleElement = document.getElementById('float-text-style');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <>
      <GameMap 
        config={mapConfigs.entrance} 
        onNpcInteraction={handleNpcInteraction}
        onPlayerPositionChange={setPlayerPosition}
      />
      
      <SafariZoneModal
        isOpen={showSafariModal}
        toggle={() => setShowSafariModal(false)}
        playerPos={playerPosition}
        spawnText={spawnText}
        onAccessGranted={handleAccessGranted}
      />
    </>
  );
};

export default Entrance;
